/**
 * Locale-aware formatting. Every number, price and date the module renders comes
 * through here.
 *
 * WHAT THIS REPLACED
 * ------------------
 *   formatPrice   `$${amount.toFixed(2)}`   — a hardcoded dollar sign, on a card
 *                                             that carries its own `currency`
 *   quantities    `String(847)`             — no grouping separator in any locale
 *   rating        `(4.4).toFixed(1)`        — a hardcoded decimal point
 *   savings       `${28}%`                  — a hardcoded percent sign, and the
 *                                             wrong side of the number in several
 *                                             locales
 *
 * None of those is wrong in en-US, which is exactly why they survived: the
 * output looked right to everyone who read it.
 *
 * ON MEMOISATION
 * --------------
 * Constructing an `Intl.NumberFormat` is expensive — it resolves locale data —
 * and the preview builds one per price per render. They are cached on their full
 * argument list. The cache is unbounded by design: its keys are (locale ×
 * currency × option-set), a small closed set for any real application, and a
 * bounded cache would evict the formatter being used in a loop.
 */

import { DEFAULT_LOCALE, resolveLocale } from './locale';

type FormatterKind = 'number' | 'date';

const CACHE = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

const cached = <T extends Intl.NumberFormat | Intl.DateTimeFormat>(
  kind: FormatterKind,
  locale: string,
  options: Intl.NumberFormatOptions | Intl.DateTimeFormatOptions,
  build: () => T
): T => {
  const key = `${kind}|${locale}|${JSON.stringify(options)}`;
  const hit = CACHE.get(key);
  if (hit) {
    return hit as T;
  }
  const formatter = build();
  CACHE.set(key, formatter);
  return formatter;
};

const numberFormat = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat =>
  cached('number', locale, options, () => new Intl.NumberFormat(locale, options));

/**
 * Money, in the currency the value is actually denominated in.
 *
 * `currency` is a parameter and not a constant because the product carries one.
 * A card that says `currency: 'EUR'` and renders `$42.00` is not a formatting
 * nit — it misstates the price.
 *
 * An unknown or malformed currency code makes `Intl` throw. Prices must not be
 * able to blank the page, so it falls back to a plain grouped number with the
 * code appended: still honest, still readable, and obviously not styled money.
 */
export const formatMoney = (
  amount: number,
  currency: string,
  locale: string = resolveLocale()
): string => {
  try {
    return numberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    return `${formatNumber(amount, locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  }
};

/** A plain number, grouped for the locale. `847` → `847`, `12847` → `12,847`. */
export const formatNumber = (
  value: number,
  locale: string = resolveLocale(),
  options: Intl.NumberFormatOptions = {}
): string => numberFormat(locale, options).format(value);

/**
 * A whole-number percentage. Takes 28 and renders "28%", not 0.28 — the card
 * stores `savingsPercent: 28`, and quietly dividing by 100 here would be the
 * kind of unit confusion that only shows up in production.
 */
export const formatPercent = (
  wholePercent: number,
  locale: string = resolveLocale()
): string =>
  numberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(wholePercent / 100);

/** A rating like 4.4 — one decimal place, with the locale's own separator. */
export const formatDecimal = (
  value: number,
  fractionDigits: number,
  locale: string = resolveLocale()
): string =>
  numberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);

export const EMPTY_DATE_PLACEHOLDER = '—';

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * `Mon-DD-YYYY` — the presentation spec R19 pins, in English.
 *
 * Kept for `en-US` specifically. R19 quotes this format and the listing's
 * oracles assert on it verbatim; `Intl` would render "May 15, 2026", which is
 * better English and a different string. Internationalising the module is not a
 * licence to silently restate a pinned requirement, so en-US keeps its format
 * and every other locale gets `Intl`.
 */
const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * The date parts, read straight off the ISO string.
 *
 * NOT via `new Date(iso).getMonth()`: `new Date('2026-05-15')` lands on UTC
 * midnight, and `getMonth()` in any negative-offset timezone reports the
 * previous day. A card edited in New York would show as edited a day earlier.
 * That property is why this stayed a regex, and it survives the move to `Intl`
 * below — the `Date` handed to the formatter is built from these parts and
 * rendered with `timeZone: 'UTC'`, so the parts that go in are the parts that
 * come out.
 */
const isoParts = (isoTimestamp: string): { year: string; month: string; day: string } | null => {
  const match = isoTimestamp.match(ISO_DATE_PATTERN);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  if (!(monthIndex >= 0 && monthIndex < 12)) {
    return null;
  }
  if (Number(day) < 1 || Number(day) > 31) {
    return null;
  }
  return { year, month, day };
};

/**
 * A clock time, in the viewer's OWN timezone — `'14:32'`, `'2:32 PM'`.
 *
 * The one place in the module that deliberately does *not* pin `timeZone: UTC`.
 * "All changes saved at 2:32 PM" is a claim about the user's afternoon; showing
 * it in UTC would be wrong by however many hours they are from Greenwich. The
 * date formatter above is the opposite case — a stored calendar date, which must
 * not shift — and the two are different questions, not an inconsistency.
 */
export const formatTimeOfDay = (
  isoTimestamp: string,
  locale: string = resolveLocale()
): string => {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return EMPTY_DATE_PLACEHOLDER;
  }
  try {
    return cached(
      'date',
      locale,
      { hour: 'numeric', minute: '2-digit' },
      () => new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' })
    ).format(parsed);
  } catch {
    return EMPTY_DATE_PLACEHOLDER;
  }
};

/**
 * `'2026-05-15T09:12:00.000Z'` → `'May-15-2026'` in en-US, and the locale's own
 * short date elsewhere. Anything unparseable renders as an em dash rather than
 * as `Invalid Date`.
 */
export const formatDate = (isoTimestamp: string, locale: string = resolveLocale()): string => {
  const parts = isoParts(isoTimestamp);
  if (!parts) {
    return EMPTY_DATE_PLACEHOLDER;
  }

  if (locale === DEFAULT_LOCALE) {
    return `${MONTH_ABBREVIATIONS[Number(parts.month) - 1]}-${parts.day}-${parts.year}`;
  }

  try {
    const utc = new Date(
      Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))
    );
    return cached(
      'date',
      locale,
      { year: 'numeric', month: 'short', day: '2-digit', timeZone: 'UTC' },
      () =>
        new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          timeZone: 'UTC',
        })
    ).format(utc);
  } catch {
    return EMPTY_DATE_PLACEHOLDER;
  }
};
